// Backend ADR 24.9.26ab D6 — `/email-preferences?t=`, the page every lifecycle
// mail's footer links to.
//
// Outside every guard, as `s/:token` is: its reader is following a link in an
// inbox and may never have signed in on this browser. The token is the whole
// credential, so the page reads it once, takes it out of the address bar with
// `history.replaceState` (the telemetry redaction closes the window before
// that runs), and shows one switch. No telemetry of its own, no consent, no
// account — and never a raw error: a `404` means "this link no longer works"
// and says what still does, and anything else is the generic failure line.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { ApiError } from '../../api/http'
import { CheckboxField } from '../../ui/Field'
import { getEmailPreferences, putEmailPreferences } from './emailPreferencesApi'
import { PageFrame } from '../../layout/frame/PageFrame'

type View =
  | { kind: 'loading' }
  | { kind: 'ready'; optOut: boolean; saving: boolean }
  | { kind: 'gone' }
  | { kind: 'failed' }

const NOT_FOUND = 404

function failure(error: unknown): View {
  return error instanceof ApiError && error.status === NOT_FOUND ? { kind: 'gone' } : { kind: 'failed' }
}

/** The token from the first render's query string, then removed from the address bar. */
function useTokenOnce(): string | null {
  const location = useLocation()
  const [token] = useState(() => new URLSearchParams(location.search).get('t'))
  useEffect(() => {
    if (token !== null) window.history.replaceState(window.history.state, '', window.location.pathname)
  }, [token])
  return token
}

function usePreferences(token: string | null) {
  const [view, setView] = useState<View>(token ? { kind: 'loading' } : { kind: 'gone' })
  useEffect(() => {
    if (!token) return
    let live = true
    getEmailPreferences(token).then(
      (prefs) => live && setView({ kind: 'ready', optOut: prefs.lifecycle_email_opt_out, saving: false }),
      (error: unknown) => live && setView(failure(error)),
    )
    return () => {
      live = false
    }
  }, [token])

  const setReceiving = (receiving: boolean) => {
    if (!token || view.kind !== 'ready') return
    const optOut = !receiving
    setView({ kind: 'ready', optOut: view.optOut, saving: true })
    putEmailPreferences(token, { lifecycle_email_opt_out: optOut }).then(
      () => setView({ kind: 'ready', optOut, saving: false }),
      (error: unknown) => setView(failure(error)),
    )
  }
  return { view, setReceiving }
}

export function EmailPreferencesPage() {
  const { t } = useTranslation()
  const { view, setReceiving } = usePreferences(useTokenOnce())
  return (
    <PageFrame width="form" as="section" className="space-y-3 py-8" data-testid="email-preferences">
      <h2 className="text-xl font-semibold">{t('email_prefs_title')}</h2>
      {view.kind === 'gone' ? <p className="text-sm">{t('email_prefs_link_gone')}</p> : null}
      {view.kind === 'failed' ? <p className="text-sm">{t('error_unknown')}</p> : null}
      {view.kind === 'ready' ? (
        <>
          <CheckboxField
            label={t('email_prefs_lifecycle_label')}
            checked={!view.optOut}
            disabled={view.saving}
            onChange={setReceiving}
          />
          <p className="text-sm text-ink-muted" data-testid="email-preferences-state">
            {t(view.optOut ? 'email_prefs_state_off' : 'email_prefs_state_on')}
          </p>
        </>
      ) : null}
    </PageFrame>
  )
}
